import React, { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2 } from "lucide-react";

type CompletedProposal = {
  id: string;
  rfpTitle: string;
  matterName: string | null;
  appointedFirm: string;
  dateAppointed: string;
  value: number;
  initialBid: number;
  rfpId: string;
};

const CompleteProposals = () => {
  // Render the title in the outlet context
  if (useOutletContext() === "title-only") {
    return "Complete Proposals";
  }
  
  const [completedProposals, setCompletedProposals] = useState<CompletedProposal[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;
  const { toast } = useToast();
  
  useEffect(() => {
    fetchCompletedProposals();
  }, []);
  
  const fetchCompletedProposals = async () => {
    try {
      setLoading(true);
      
      // Fetch RFPs with COMPLETED status for the current buyer
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      
      // Get completed RFPs with their decisions
      const { data: rfpsData, error: rfpsError } = await supabase
        .from("rfps")
        .select(`
          id,
          title,
          matter_name
        `)
        .eq("buyer_id", user.id)
        .eq("status", "COMPLETED");
      
      if (rfpsError) throw rfpsError;
      
      if (!rfpsData || rfpsData.length === 0) {
        setCompletedProposals([]);
        setLoading(false);
        return;
      }
      
      // Fetch decisions for these RFPs
      const rfpIds = rfpsData.map(r => r.id);
      const { data: decisionsData, error: decisionsError } = await supabase
        .from("rfp_decisions")
        .select(`
          rfp_id,
          winning_proposal_id,
          decided_at
        `)
        .in("rfp_id", rfpIds);
      
      if (decisionsError) throw decisionsError;
      
      // Fetch winning proposals
      const winningProposalIds = (decisionsData || [])
        .filter(d => d.winning_proposal_id)
        .map(d => d.winning_proposal_id);
      
      let proposalsMap: Record<string, { supplier_company_name: string; initial_bid_amount: number }> = {};
      
      if (winningProposalIds.length > 0) {
        const { data: proposalsData, error: proposalsError } = await supabase
          .from("proposals")
          .select("id, supplier_company_name, initial_bid_amount")
          .in("id", winningProposalIds);
        
        if (proposalsError) throw proposalsError;
        
        proposalsMap = (proposalsData || []).reduce((acc, p) => {
          acc[p.id] = { supplier_company_name: p.supplier_company_name, initial_bid_amount: p.initial_bid_amount };
          return acc;
        }, {} as Record<string, { supplier_company_name: string; initial_bid_amount: number }>);
      }
      
      // Fetch the latest confirmed bids for winning proposals
      let bidsMap: Record<string, number> = {};
      
      if (winningProposalIds.length > 0) {
        const { data: bidsData, error: bidsError } = await supabase
          .from("bids")
          .select("proposal_id, amount")
          .in("proposal_id", winningProposalIds)
          .eq("status", "confirmed")
          .order("created_at", { ascending: false });
        
        if (bidsError) throw bidsError;
        
        // Get the latest confirmed bid for each proposal
        (bidsData || []).forEach(bid => {
          if (!bidsMap[bid.proposal_id]) {
            bidsMap[bid.proposal_id] = bid.amount;
          }
        });
      }
      
      // Build completed proposals list
      const completed: CompletedProposal[] = rfpsData.map(rfp => {
        const decision = (decisionsData || []).find(d => d.rfp_id === rfp.id);
        const winningProposal = decision?.winning_proposal_id ? proposalsMap[decision.winning_proposal_id] : null;
        const finalBid = decision?.winning_proposal_id ? bidsMap[decision.winning_proposal_id] : null;
        const initialBid = winningProposal?.initial_bid_amount || 0;
        
        return {
          id: decision?.winning_proposal_id || rfp.id,
          rfpId: rfp.id,
          rfpTitle: rfp.title,
          matterName: rfp.matter_name,
          appointedFirm: winningProposal?.supplier_company_name || "N/A",
          dateAppointed: decision?.decided_at || "",
          value: finalBid || initialBid,
          initialBid: initialBid,
        };
      });
      
      setCompletedProposals(completed);
    } catch (error) {
      console.error("Error fetching completed proposals:", error);
      toast({
        title: "Error loading proposals",
        description: "There was a problem loading your completed proposals.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Format currency values
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return "Invalid date";
    }
  };

  // Calculate savings
  const calculateSavings = (initial: number, final: number) => {
    return initial - final;
  };

  const handleDeleteRfp = async (rfpId: string) => {
    try {
      // Delete related records first (due to foreign key constraints)
      await supabase.from("bids").delete().eq("rfp_id", rfpId);
      await supabase.from("proposals").delete().eq("rfp_id", rfpId);
      await supabase.from("supplier_invitations").delete().eq("rfp_id", rfpId);
      await supabase.from("rfp_decisions").delete().eq("rfp_id", rfpId);
      
      // Finally delete the RFP
      const { error } = await supabase.from("rfps").delete().eq("id", rfpId);
      
      if (error) throw error;
      
      toast({
        title: "Proposal deleted",
        description: "The completed proposal has been successfully deleted.",
      });
      
      fetchCompletedProposals();
    } catch (error) {
      console.error("Error deleting RFP:", error);
      toast({
        title: "Error",
        description: "Failed to delete the proposal. Please try again.",
        variant: "destructive",
      });
    }
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(completedProposals.length / itemsPerPage);
  const currentProposals = completedProposals.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-4 w-64 mb-6" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-semibold mb-4">Complete Proposals</h2>
        <p className="text-gray-600 mb-6">
          View and manage your complete proposals here.
        </p>
        
        {completedProposals.length === 0 ? (
          <div className="p-8 border border-dashed border-gray-300 rounded-lg text-center">
            <p className="text-gray-500">Your complete proposals will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name of Matter</TableHead>
                    <TableHead>Law Firm Appointed</TableHead>
                    <TableHead>Date Appointed</TableHead>
                    <TableHead>Value of Proposal</TableHead>
                    <TableHead>Amount Saved</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentProposals.map((proposal) => (
                    <TableRow key={proposal.id}>
                      <TableCell className="font-medium">
                        {proposal.matterName || proposal.rfpTitle || "Untitled Proposal"}
                      </TableCell>
                      <TableCell>{proposal.appointedFirm}</TableCell>
                      <TableCell>{formatDate(proposal.dateAppointed)}</TableCell>
                      <TableCell>{formatCurrency(proposal.value)}</TableCell>
                      <TableCell>{formatCurrency(calculateSavings(proposal.initialBid, proposal.value))}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" className="bg-white" asChild>
                            <Link to={`/auctions/${proposal.rfpId}/bids`}>
                              View Details
                            </Link>
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Completed Proposal</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{proposal.matterName || proposal.rfpTitle}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteRfp(proposal.rfpId)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                  
                  {Array.from({length: totalPages}).map((_, index) => (
                    <PaginationItem key={index}>
                      <PaginationLink
                        isActive={currentPage === index + 1}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompleteProposals;